import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { errorState, HttpRequestState, httpRequestStates, loadedState } from 'ngx-http-request-state';
import { defer, from, Observable, of, switchMap } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { getLogger } from 'tofe-core';
import { CopTOFEConfiguration, ErrorMessage, ProcessData, ProcessInfo, ProcessService } from 'top-api-sdk-angular';

import { createStrongAuthSession, isStrongAuthError } from './models/strong-auth-error';
import { SessionInfoRequestState } from './top-session.service';
import { SessionInfo } from './types';
import { isMobile } from './utils';

const log = getLogger('[Resolver][Process]').debug;

const DEFAULT_ERROR_MESSAGE = 'TOFE.PROCESS_RESOLVER.GENERIC_ERROR';

export class StartError extends Error {
  browserCookieEnabled?: boolean;
}

/**
 * t(TOFE.PROCESS_RESOLVER.GENERIC_ERROR.TITLE)
 * t(TOFE.PROCESS_RESOLVER.GENERIC_ERROR.DESCRIPTION)
 * t(TOFE.PROCESS_RESOLVER.AUTH_FAILED.TITLE)
 * t(TOFE.PROCESS_RESOLVER.AUTH_FAILED.DESCRIPTION)
 * t(TOFE.PROCESS_RESOLVER.USED_TOKEN.TITLE)
 * t(TOFE.PROCESS_RESOLVER.USED_TOKEN.DESCRIPTION)
 * t(TOFE.PROCESS_RESOLVER.EXPIRED_TOKEN.TITLE)
 * t(TOFE.PROCESS_RESOLVER.EXPIRED_TOKEN.DESCRIPTION)
 */
const getStartErrorMessage = (payload: ErrorMessage) => {
  switch (payload?.errorKey) {
    case 'SK099':
      return 'TOFE.PROCESS_RESOLVER.AUTH_FAILED';
    case 'COP-0001':
      return 'TOFE.PROCESS_RESOLVER.USED_TOKEN';
    case 'COP-0200':
      return 'TOFE.PROCESS_RESOLVER.DIFFERENT_BROWSER';
    case 'INVALID_TOKEN':
      if (payload.errorMessage?.includes('expired')) {
        return 'TOFE.PROCESS_RESOLVER.EXPIRED_TOKEN';
      } else {
        return 'TOFE.PROCESS_RESOLVER.INVALID_TOKEN';
      }
    case null:
    case undefined:
      return 'TOFE.PROCESS_RESOLVER.START_ERROR';
    default:
      return DEFAULT_ERROR_MESSAGE;
  }
};

const interceptStartErrors = (x: HttpRequestState<ProcessData>) => {
  if (x.error instanceof HttpErrorResponse) {
    const originalError = x.error;
    const payload = originalError.error as ErrorMessage;

    log('[interceptStartErrors] HTTP Error:', originalError);

    // Check if this is a strong authentication error first
    if (isStrongAuthError(payload)) {
      // For strong auth errors, mark the error as a strong auth error
      log('[interceptStartErrors] Strong auth error detected');
      const error = new StartError(getStartErrorMessage(payload));
      return { ...x, error, originalError, isStrongAuthError: true };
    }

    // Fall back to normal error handling for non-strong-auth errors
    const error = new StartError(getStartErrorMessage(payload));
    return { ...x, error, originalError };
  }
  return x;
};

export const processResolver: ResolveFn<Observable<Observable<SessionInfoRequestState>>> = (route) => {
  const api = inject(ProcessService);
  const id = route.queryParamMap.get('id') as string;
  log('hit', id);
  return defer(() =>
    of(getProcessObservable(api, id).pipe(map(interceptWelcomePage(route)), map(interceptRouteErrors(route)))),
  );
};

const mergeProcessInfoInSession =
  (id: string, startResult: HttpRequestState<ProcessData>) => (processResult: HttpRequestState<ProcessInfo>) => {
    if (processResult.value) {
      return loadedState<SessionInfo>({
        id,
        startData: startResult.value,
        processData: processResult.value,
      });
    } else {
      return processResult as HttpRequestState<SessionInfo>;
    }
  };

export const interceptWelcomePage = (route: ActivatedRouteSnapshot) => (res: SessionInfoRequestState) => {
  const welcomePageParam = route.queryParamMap.get('welcomePage');
  if (welcomePageParam === 'false' && res.value?.processData?.tofeConfiguration) {
    return {
      ...res,
      value: {
        ...res.value,
        processData: {
          ...res.value.processData,
          tofeConfiguration: {
            ...res.value.processData.tofeConfiguration,
            welcomePage: 'false',
          },
        },
      },
    };
  }
  return res;
};

export const interceptProcessErrors = async (
  res: HttpRequestState<SessionInfo>,
): Promise<HttpRequestState<SessionInfo>> => {
  // `ProcessInfo.tofeConfiguration` is typed as the base `ICopTOFEConfiguration`
  // (just `{ type: string }`); `mobileOnly` lives on the concrete `CopTOFEConfiguration`.
  const isMobileOnly = (res.value?.processData?.tofeConfiguration as CopTOFEConfiguration | undefined)?.mobileOnly;
  const isMobileOnlyError = isMobileOnly && !(await isMobile());
  if (isMobileOnlyError) {
    const error = new StartError('mobile_only');
    error.browserCookieEnabled = res.value?.processData?.browserCookieConfiguration?.enabled ?? false;
    return { isLoading: false, error };
  }
  return res;
};

const getProcessObservable = (api: ProcessService, id?: string): Observable<SessionInfoRequestState> => {
  if (!id) {
    return of(errorState<SessionInfo>(new StartError(DEFAULT_ERROR_MESSAGE)));
  }
  return api.startProcess(id).pipe(
    httpRequestStates(),
    map(interceptStartErrors),
    switchMap((startResult) => {
      if (startResult.isLoading) {
        return of(startResult as HttpRequestState<SessionInfo>);
      }
      if (startResult.error) {
        // Check if this is a strong auth error and create session if so
        if ('isStrongAuthError' in startResult && startResult.isStrongAuthError) {
          log('Creating strong auth session for error');
          const strongAuthSession = createStrongAuthSession(id);
          return of(loadedState<SessionInfo>(strongAuthSession));
        }
        return of(startResult as HttpRequestState<SessionInfo>);
      }
      const sessionKey = startResult.value?.sessionKey;
      if (!sessionKey) {
        // it is still possible that start returns a valid response but without a sessionKey
        return of({
          ...errorState<SessionInfo>(new StartError(DEFAULT_ERROR_MESSAGE)),
          originalValue: startResult.value,
        });
      }
      api.configuration.credentials['CopApiKey'] = 'COP ' + sessionKey;
      return api.getProcess(id).pipe(httpRequestStates(), map(mergeProcessInfoInSession(id, startResult)));
    }),
    tap((res) => {
      log('getProcessObservable', res);
    }),
    // TOP-17544
    map((x) => interceptStartErrors(x as unknown as HttpRequestState<ProcessData>) as HttpRequestState<SessionInfo>),
    switchMap((res) => from(interceptProcessErrors(res))),
    shareReplay(1),
  );
};

export const interceptRouteErrors = (route: ActivatedRouteSnapshot) => (res: SessionInfoRequestState) => {
  if (res.error instanceof StartError) {
    const spidError = route.queryParamMap.get('spidError') as string;
    if (spidError) {
      return {
        ...res,
        error: new StartError(getSpidErrorMessage(spidError)),
      } as SessionInfoRequestState;
    }
  }
  return res;
};

/**
 * t(TOFE.PROCESS_RESOLVER.SPID_ERROR.TITLE)
 * t(TOFE.PROCESS_RESOLVER.SPID_ERROR.DESCRIPTION)
 * t(TOFE.PROCESS_RESOLVER.SPID_ERROR_DATA_MISMATCH.TITLE)
 * t(TOFE.PROCESS_RESOLVER.SPID_ERROR_DATA_MISMATCH.DESCRIPTION)
 */
export const getSpidErrorMessage = (spidError: string) => {
  if (spidError === '514') {
    return 'TOFE.PROCESS_RESOLVER.SPID_ERROR_DATA_MISMATCH';
  }
  return 'TOFE.PROCESS_RESOLVER.SPID_ERROR';
};
